"use client";

const sharedCookieOptions = {
	// We want to have access to the cookie on jace.ai LP
	domain:
		process.env.NEXT_PUBLIC_VERCEL_ENV === "production"
			? ".jace.ai"
			: undefined,
	path: "/",
	secure: process.env.NEXT_PUBLIC_VERCEL_ENV === "production",
	sameSite: "lax",
} as const;

/**
 * Google One Tap Sign-In Component
 *
 * This component implements Google One Tap sign-in with optional FedCM (Federated Credential Management) support.
 * FedCM provides enhanced privacy by eliminating third-party cookies, but has limitations on localhost.
 *
 * Important notes:
 * - FedCM is automatically disabled on localhost due to CORS restrictions from Google's endpoints
 * - On localhost, the component will show the Google Sign-In button as a fallback
 * - In production, FedCM will work normally if the browser supports it
 * - If used in cross-origin iframes, parent frames must include: allow="identity-credentials-get"
 * - CSP directives may need updating if your site uses Content Security Policy
 * - Custom positioning is not supported with FedCM - the browser controls prompt placement
 * - Ensure your domain is added to authorized JavaScript origins in Google Cloud Console
 * - FedCM requires Chrome 117+ and third-party sign-in must be enabled in browser settings
 *
 * Common issues:
 * - "Origin not allowed" error (403):
 *   1. Go to https://console.cloud.google.com/
 *   2. Navigate to APIs & Services → Credentials
 *   3. Find your OAuth 2.0 Client ID
 *   4. Add your development URL (e.g., http://localhost:3000) to "Authorized JavaScript origins"
 * - CORS errors on localhost: This is expected. FedCM requires specific CORS headers from Google
 *   which are not available for localhost. The component automatically falls back to button mode.
 * - "AbortError": The component handles this automatically by showing a fallback button
 * - One Tap not showing: Falls back to Google Sign-In button automatically
 *
 * @see https://developers.google.com/identity/gsi/web/guides/fedcm-migration
 * @see https://developers.google.com/privacy-sandbox/blog/fedcm-chrome-125-updates
 */

import Cookies from "js-cookie";
import { useCallback, useEffect, useState } from "react";
import { ACTIVE_USER_ID_COOKIE, AUTH_TOKEN_COOKIE } from "../../../constants";
import { router } from "../../../router";
import { Script } from "../../script";

interface GoogleOneTapProps {
	redirectUrl: string;
}

declare global {
	interface Window {
		google?: any;
	}
}

export function GoogleOneTap({
	redirectUrl,
}: GoogleOneTapProps): React.ReactElement | null {
	const [isInitialized, setIsInitialized] = useState(false);
	// Show button by default on localhost since One Tap won't work due to CORS
	const [showFallbackButton, setShowFallbackButton] = useState(
		typeof window !== "undefined" && window.location.hostname === "localhost",
	);

	const handleCredential = useCallback(
		async (cred: string) => {
			try {
				const res = await fetch(
					`${process.env.NEXT_PUBLIC_API_URL}/api/auth/google_one_tap`,
					{
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ credential: cred }),
					},
				);
				if (!res.ok) throw new Error("Failed to sign in");
				const data = (await res.json()) as { jwt: string; user_id: string };

				// Use the user_id from the response directly instead of parsing JWT
				const userId = data.user_id;

				Cookies.set(`${AUTH_TOKEN_COOKIE}_${userId}`, data.jwt, {
					...sharedCookieOptions,
					// js-cookie uses expires in days, not maxAge in seconds
					expires: 360,
				});
				Cookies.set(ACTIVE_USER_ID_COOKIE, userId, {
					...sharedCookieOptions,
					// js-cookie uses expires in days, not maxAge in seconds
					expires: 360,
				});

				// Emit custom event to notify Auth Provider that cookies have been updated
				window.dispatchEvent(new Event("auth-cookies-updated"));

				// Small delay to ensure cookies are persisted before redirect
				setTimeout(() => {
					router.push(redirectUrl);
				}, 100);
			} catch (e) {
				console.error("Google One Tap sign-in failed:", e);
			}
		},
		[router, redirectUrl],
	);

	useEffect(() => {
		// Prevent double initialization in React StrictMode
		if (isInitialized) return;

		// Check if script is loaded
		const checkGoogleLoaded = () => {
			if (!window.google?.accounts?.id) {
				console.log("Google Sign-In script not loaded yet, checking again...");
				// Script not loaded yet, check again
				setTimeout(checkGoogleLoaded, 100);
				return;
			}

			console.log("Google Sign-In script loaded, initializing One Tap...");
			console.log("Client ID:", process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID);

			try {
				// Initialize Google Sign-In (FedCM disabled to avoid network errors)
				window.google.accounts.id.initialize({
					client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
					callback: (response: any) => {
						handleCredential(response.credential);
					},
					// FedCM disabled - can cause network errors and isn't essential
					use_fedcm_for_prompt: false,
					// Standard configuration
					auto_select: true, // Let user explicitly choose
					context: "signin",
					// Add comprehensive error handling
					error_callback: () => {
						console.log(
							"One Tap sign-in failed or was closed, showing button fallback",
						);
						setShowFallbackButton(true);
					},
					// Hosted domain restriction (optional - uncomment if needed)
					// hosted_domain: 'yourdomain.com',
				});

				// Try to show One Tap prompt
				window.google.accounts.id.prompt((notification: any) => {
					// With FedCM, we won't get detailed status, but we can still handle basic cases
					if (notification?.getMomentType?.() === "skipped") {
						setShowFallbackButton(true);
					}
				});

				setIsInitialized(true);
			} catch (error) {
				console.error("Failed to initialize Google One Tap:", error);
				setShowFallbackButton(true);
			}
		};

		// Start checking for Google script
		checkGoogleLoaded();
	}, [isInitialized, handleCredential]);

	return <Script async src="https://accounts.google.com/gsi/client" />;
}
